/**
 * Request Middleware
 *
 * Individual Redux middleware functions for request management:
 * deduplication, priority queuing, timeout, route change, retry, and logging.
 */

import { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue, isPending, isFulfilled } from '@reduxjs/toolkit';
import { nanoid } from '@reduxjs/toolkit';
import { RequestPriority, RequestQueueManager } from './request-queue';
import type { RequestMeta } from './request-queue';
import { apiLogger } from '@/core/utils/logger';

/**
 * Request deduplication middleware
 */
export function createRequestDeduplicationMiddleware(requestQueue: RequestQueueManager): Middleware {
  return () => (next) => (action) => {
    if (isPending(action) && action.meta?.arg) {
      const endpoint = action.type.split('/')[1] || 'unknown';
      const params = action.meta.arg;

      const existing = requestQueue.checkDuplicate(endpoint, params);
      if (existing) {
        apiLogger.info('[Dedup] Skipping duplicate request:', endpoint);
        return existing;
      }

      // RTK Query handles its own deduplication, just pass through
      return next(action);
    }

    return next(action);
  };
}

/**
 * Request priority middleware
 */
export function createRequestPriorityMiddleware(requestQueue: RequestQueueManager): Middleware {
  return () => (next) => (action) => {
    if (isPending(action)) {
      const priority = RequestPriority.NORMAL; // Default since priority isn't in RTK Query meta
      const endpoint = action.type.split('/')[1] || 'unknown';

      const requestMeta: RequestMeta = {
        id: nanoid(),
        endpoint,
        priority,
        timestamp: Date.now(),
        retries: 0,
        maxRetries: 3,
      };

      requestQueue.enqueue(requestMeta);
    }

    return next(action);
  };
}

/**
 * Request timeout middleware
 */
export const requestTimeoutMiddleware: Middleware = () => (next) => (action) => {
  // Add timeout logic here if needed
  return next(action);
};

/**
 * Route change middleware
 */
export const routeChangeMiddleware: Middleware = () => (next) => (action: unknown) => {
  // Cancel low-priority requests on route changes
  if ((action as { type?: string }).type === '@@router/LOCATION_CHANGE') {
    apiLogger.info('[Router] Route changed, managing requests');
    // Implementation would cancel low-priority requests
  }

  return next(action);
};

/**
 * Retry middleware
 */
export const retryMiddleware: Middleware = () => (next) => (action) => {
  // Add retry logic here if needed
  return next(action);
};

/**
 * Request logging middleware
 */
export const requestLoggingMiddleware: Middleware = () => (next) => (action: unknown) => {
  const typedAction = action as { type?: string; meta?: { originalArgs?: unknown; arg?: { originalArgs?: unknown } }; payload?: unknown };
  if (typedAction.type?.includes('api/')) {
    const timestamp = new Date().toISOString();
    const endpoint = typedAction.type.split('/')[1] || 'unknown';

    if (isPending(action)) {
      const args = typedAction.meta?.originalArgs || typedAction.meta?.arg?.originalArgs;
      apiLogger.info(`[API] ${timestamp} REQUEST: ${endpoint}`, args);
    } else if (isFulfilled(action)) {
      apiLogger.info(`[API] ${timestamp} SUCCESS: ${endpoint}`, typedAction.payload);
    } else if (isRejectedWithValue(action)) {
      const rawError = (typedAction as { payload?: unknown; error?: unknown }).payload || (typedAction as { payload?: unknown; error?: unknown }).error;
      const errorObj = rawError as Record<string, unknown> | undefined;

      // Extract error details from various possible structures
      const errorMessage = errorObj?.message ||
                           (errorObj?.data as Record<string, unknown>)?.message ||
                           (errorObj?.data as Record<string, unknown>)?.error ||
                           errorObj?.error ||
                           (rawError instanceof Error ? rawError.message : null) ||
                           'Unknown error';
      const status = errorObj?.status ||
                     (errorObj?.data as Record<string, unknown>)?.status ||
                     errorObj?.originalStatus ||
                     'unknown';

      const errorDetails: Record<string, unknown> = {
        status,
        message: errorMessage,
        data: errorObj?.data,
        name: rawError instanceof Error ? rawError.name : undefined,
      };

      // Include the raw error if it has useful info
      if (errorObj && Object.keys(errorObj).length > 0) {
        errorDetails['raw'] = errorObj;
      }

      apiLogger.error(`[API] ${timestamp} ERROR: ${endpoint}`, errorDetails);
    }
  }

  return next(action);
};
